// Hook personalizado para gestionar el formulario de propiedades

import { useState } from 'react';
import { getPropertyStatus } from '../../../utils/propertyHelpers';

/**
 * Hook personalizado para gestionar el estado y lógica del formulario de propiedades
 */
export const usePropertyForm = (property, properties, bids) => {
  const isEditing = !!property;
  const propertyBids = isEditing ? bids?.filter(b => b.propertyId === property.id) || [] : [];
  const hasBids = propertyBids.length > 0;

  // El precio solo es editable si la propiedad está en UPCOMING (antes de cualquier fase)
  const propertyStatus = isEditing ? getPropertyStatus(property) : 'UPCOMING';
  const isPriceEditable = propertyStatus === 'UPCOMING';

  // Función para convertir fecha UTC a formato datetime-local en hora local
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Estado principal del formulario
  // ✅ biddingStartDate y deadline se configuran al abrir la subasta abierta, NO en creación
  // ✅ minIncrement y maxBidPercentage se configuran al abrir subasta abierta
  const [formData, setFormData] = useState({
    title: property?.title || '',
    description: property?.description || '',
    floorPlanUrl: property?.floorPlanUrl || '',
    videoUrl: property?.videoUrl || '',
    photoUrls: property?.photoUrls || [],
    startPrice: property?.originalStartPrice || property?.startPrice || '',
    minAbsolute: property?.minAbsolute || '',
    adminEmails: property?.adminEmails || [],
    lastChanceDuration: property?.last_chance_duration || 48,
    calendlyUrl: property?.calendlyUrl || '',
    propertyMetadata: property?.propertyMetadata
      ? JSON.stringify(property.propertyMetadata, null, 2)
      : '',
    translationsEnabled: property?.translationsEnabled || false,
  });

  // Estados auxiliares
  const [photoUrlsText, setPhotoUrlsText] = useState(
    property?.photoUrls ? property.photoUrls.join('\n') : ''
  );

  const [adminEmailsText, setAdminEmailsText] = useState(
    property?.adminEmails ? property.adminEmails.join('\n') : ''
  );

  // Estado para URLs de documentos (formato: Nombre|URL por línea)
  const [documentUrlsText, setDocumentUrlsText] = useState(
    property?.documentUrls 
      ? property.documentUrls.map(doc => `${doc.name}|${doc.url}`).join('\n') 
      : ''
  );

  const [error, setError] = useState('');

  /**
   * Manejar cambios en campos del formulario
   */
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  /**
   * Validar y preparar datos para envío
   * ✅ Las fechas (biddingStartDate, deadline) se configuran al abrir subasta abierta
   */
  const validateAndPrepare = () => {
    setError('');

    // Procesar URLs de fotos
    const photoUrlsArray = photoUrlsText
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0)
      .slice(0, 50);

    // ✅ VALIDACIÓN MULTIMEDIA: Al menos una opción debe estar configurada
    const hasFloorPlan = formData.floorPlanUrl && formData.floorPlanUrl.trim().length > 0;
    const hasVideo = formData.videoUrl && formData.videoUrl.trim().length > 0;
    const hasPhotos = photoUrlsArray.length > 0;

    if (!hasFloorPlan && !hasVideo && !hasPhotos) {
      setError('Debes configurar al menos una opción multimedia: plano, vídeo o fotos');
      return null;
    }

    // Procesar emails de administrador
    const adminEmailsArray = adminEmailsText
      .split('\n')
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@'));

    // Procesar URLs de documentos (formato: Nombre|URL)
    const documentUrlsArray = documentUrlsText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0 && line.includes('|'))
      .map(line => {
        const [name, url] = line.split('|').map(s => s.trim());
        return { name, url };
      })
      .filter(doc => doc.name && doc.url);

    // Parsear metadata JSON
    let metadataObject = {};
    if (formData.propertyMetadata && formData.propertyMetadata.trim()) {
      try {
        metadataObject = JSON.parse(formData.propertyMetadata);
      } catch (e) {
        setError('El formato del JSON de metadata no es válido. Por favor, revisa la sintaxis.');
        return null;
      }
    }

    // Preparar objeto de propiedad
    // ✅ NO incluir biddingStartDate ni deadline - se configuran al abrir subasta abierta
    // ✅ NO incluir minIncrement ni maxBidPercentage - se configuran al abrir subasta abierta
    const propertyData = {
      title: formData.title,
      description: formData.description,
      floorPlanUrl: formData.floorPlanUrl || null,
      videoUrl: formData.videoUrl || null,
      photoUrls: photoUrlsArray,
      adminEmails: adminEmailsArray,
      startPrice: parseFloat(formData.startPrice),
      minAbsolute: parseFloat(formData.minAbsolute),
      lastChanceDuration: parseInt(formData.lastChanceDuration) || 48,
      calendlyUrl: formData.calendlyUrl || '',
      propertyMetadata: metadataObject,
      documentUrls: documentUrlsArray,
      translationsEnabled: formData.translationsEnabled || false,
    };

    return propertyData;
  };

  /**
   * Crear nueva propiedad
   * ✅ FIX: Ahora pasa directamente el objeto de propiedad a onSave
   */
  const createNewProperty = async (onSave) => {
    const propertyData = validateAndPrepare();
    if (!propertyData) return;

    // Crear objeto de nueva propiedad (sin id - Supabase lo generará)
    const newProperty = {
      ...propertyData,
      createdAt: new Date().toISOString()
    };
    
    // ✅ FIX: Pasar el objeto directamente, no un array
    await onSave(newProperty);
  };

  /**
   * Actualizar propiedad existente
   * ✅ FIX: Ahora pasa directamente el objeto de propiedad actualizado a onSave
   */
  const updateExistingProperty = async (onSave) => {
    const propertyData = validateAndPrepare();
    if (!propertyData) return;

    // Construir objeto actualizado preservando campos originales importantes
    const updatedProperty = {
      ...property,           // Todos los campos originales (id, createdAt, isActive, etc.)
      ...propertyData,       // Sobrescribir con datos del formulario
      id: property.id,       // Asegurar explícitamente que el id está presente
    };
    
    // ✅ FIX: Pasar el objeto directamente, no un array
    await onSave(updatedProperty);
  };

  return {
    // Estado
    formData,
    photoUrlsText,
    setPhotoUrlsText,
    adminEmailsText,
    setAdminEmailsText,
    documentUrlsText,
    setDocumentUrlsText,
    error,
    setError,
    isEditing,
    hasBids,
    isPriceEditable,

    // Funciones
    handleChange,
    createNewProperty,
    updateExistingProperty,
    formatDateForInput
  };
};